import { useState, useEffect } from "react";
import {
  CloseButton,
  Combobox,
  InputBase,
  Loader,
  useCombobox,
} from "@mantine/core";
import { searchValueListByCode } from "../../../api";
import { useDebouncedValue } from "@mantine/hooks";

function getAsyncData(attrib, searchQuery = "", limit = 50) {
  const listCode = attrib.typeParameters.find(
    (p) => p.name === "LIST_CODE"
  )?.value;
  return searchValueListByCode(
    attrib.destinationMarketplaceId,
    listCode,
    searchQuery,
    limit
  ).catch(() => null);
}

export default function ValueListInput({
  form,
  attrib,
  value = "",
  error,
  limit = null,
}) {
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState(value || "");
  const [debouncedSearch] = useDebouncedValue(search, 300);

  useEffect(() => {
    setSearch(value || "");
  }, [value]);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => {
      // Load initial data when dropdown opens
      if (data.length === 0 && !loading) {
        setLoading(true);
        getAsyncData(attrib, "", limit ?? 50).then((response) => {
          if (!response?.values) {
            setData([]);
            setListError("Value list not found");
            setLoading(false);
            return;
          }
          setListError(null);
          const mappedData = response.values.map((p) => ({
            value: p.code,
            label: p.label,
          }));
          setData(mappedData);
          setLoading(false);
          combobox.resetSelectedOption();
        });
      }
    },
  });

  // Fetch data when debounced search changes
  useEffect(() => {
    if (debouncedSearch !== "" || data.length > 0) {
      setLoading(true);
      getAsyncData(attrib, debouncedSearch, limit ?? 50).then((response) => {
        if (!response?.values) {
          setData([]);
          setListError("Value list not found");
          setLoading(false);
          return;
        }
        setListError(null);
        const mappedData = response.values.map((p) => ({
          value: p.code,
          label: p.label,
        }));
        setData(mappedData);
        setLoading(false);
        combobox.updateSelectedOptionIndex();
      });
    }
  }, [debouncedSearch, attrib, limit]);

  const options = data.map((item) => (
    <Combobox.Option value={item.label} key={item.value}>
      {item.label}
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(val) => {
        form.setFieldValue(attrib.code, val);
        setSearch(val);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <InputBase
          rightSection={
            loading ? (
              <Loader size={18} />
            ) : value ? (
              <CloseButton
                size="sm"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  form.setFieldValue(attrib.code, "");
                  setSearch("");
                }}
                aria-label="Clear value"
              />
            ) : (
              <Combobox.Chevron />
            )
          }
          onClick={() => combobox.openDropdown()}
          onFocus={() => combobox.openDropdown()}
          onBlur={() => {
            combobox.closeDropdown();
            setSearch(value || "");
          }}
          rightSectionPointerEvents="all"
          placeholder="Search value"
          value={search}
          error={listError || error}
          onChange={(event) => {
            combobox.openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options mah={600} style={{ overflowY: "auto" }}>
          {loading ? (
            <Combobox.Empty>Loading....</Combobox.Empty>
          ) : options.length > 0 ? (
            options
          ) : (
            <Combobox.Empty>No matches</Combobox.Empty>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
